import { snapchat } from '@/gen/proto/protos';
import IColorGroupTones = snapchat.bitmoji.api.IColorGroupTones;
import MixAndMatchHelper from '@/services/MixAndMatchHelper';
import ITone = snapchat.bitmoji.api.ITone;

export enum ColourPickerOptionsType {
  /**
   * Colour picker options for fashion items (e.g. tops, bottoms, etc.).
   * Specified as a collection of category specific tone mappings (e.g. top_tone1 -> "12345")
   */
  FASHION = 'fashion',
  /**
   * Colour picker options for facial feature items (e.g. earrings, noserings, etc.).
   * Specified as a list of colour ids from the associated facial feature "group"
   */
  FACIAL_FEATURES = 'facial_features',
  /**
   * Colour picker options for trait items (e.g. hats, glasses, etc.)
   * Specified as a list of tones/hex colour arrays (e.g. [ [12345, 54321], [11111, 22222], etc.)
   */
  TRAITS = 'traits',
}

/**
 * A single colour picker option/swatch set may be represented by a:
 *  - number array (comprised of an rgb int)
 *  - an instance of the ITone proto object
 *  - an id for colours in a facial features group
 */
export type ColourPickerOption = number[] | ITone | string;

export interface ColourPickerConfig {
  type: ColourPickerOptionsType;
  options: ColourPickerOption[];
  secondaryColours?: number[];
  defaultColourOptionIndex?: number;
}

// For Fashion

/**
 * Processed colour picker options for mix and match categories (e.g. top, bottom)
 */
export class FashionColourPickerConfig implements ColourPickerConfig {
  type = ColourPickerOptionsType.FASHION;

  /**
   *   A collection of ranked swatch sets describing the selection of colours for these options/palette.
   *   Values are in the form of an IColourGroupTone specifying a mapping between category tone labels
   *   (e.g. top_tone1, bottom_tone3, etc.) to an integer representation of the hex rgb.
   */
  options: ITone[];

  /**
   * Indices for secondary colours for each option's icon. If absent only the default (i.e. index: 0)
   * colour is depicted in the corresponding icon.
   */
  secondaryColours?: number[];

  constructor(swatchSets: ITone[], secondaryColours?: number[]) {
    this.options = swatchSets;
    this.secondaryColours = secondaryColours;
  }
}

// For traits

/**
 * Processed colour picker options for trait categories (e.g. hats, glasses)
 */
export class TraitColourPickerConfig implements ColourPickerConfig {
  type = ColourPickerOptionsType.TRAITS;

  /**
   *   A collection of ranked swatch sets describing the selection of colours for these options/palette.
   *   Values are integer representations of hex rgb colour codes.
   */
  options: number[][];

  /**
   * Indices for secondary colours for each option's icon. If absent only the default (i.e. index: 0)
   * colour is depicted in the corresponding icon.
   */
  secondaryColours?: number[];

  /**
   * The index for the default colour option in this config. Intended to specify an element within the
   * @link options array.
   */
  defaultColourOptionIndex?: number;

  constructor(
    swatchSets: number[][],
    secondaryColours?: number[],
    defaultColourOptionIndex?: number,
  ) {
    this.options = swatchSets;
    this.secondaryColours = secondaryColours;
    this.defaultColourOptionIndex = defaultColourOptionIndex;
  }
}

// For facial features

/**
 * Processed colour picker options facial feature categories (e.g. earrings, noserings).
 * This type of colour picker option omits secondary colour information in favour of added
 * logic based around the 'iconColours' for
 */
export class FacialFeatureColourPickerConfig implements ColourPickerConfig {
  type = ColourPickerOptionsType.FACIAL_FEATURES;

  /**
   * A collection of colour ids derived from a facial feature group.
   */
  options: string[];

  constructor(groupColourOptionIds: string[]) {
    this.options = groupColourOptionIds;
  }
}
